import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Columns2, Palette, RectangleHorizontal, Square, Trash2 } from 'lucide-react';
import { DashboardModuleColor, DashboardModuleSpan } from '../../types';
import { DASHBOARD_CARD_THEMES, getDashboardCardTheme } from '../../utils/dashboardCardThemes';
import { GrabHandleIcon } from '../icons/GrabHandleIcon';

interface ModuleCardProps {
  title: string;
  description: string;
  icon: string;
  children: React.ReactNode;
  moduleColor?: DashboardModuleColor;
  span?: DashboardModuleSpan;
  placeholderRowSpan?: number;
  isDragging?: boolean;
  isPreviewSlot?: boolean;
  fillHeight?: boolean;
  showSpanControl?: boolean;
  onRemove?: () => void;
  onColorChange?: (moduleColor: DashboardModuleColor) => void;
  onSpanChange?: (span: DashboardModuleSpan) => void;
  isAdmin?: boolean;
}

const ModuleCard: React.FC<ModuleCardProps> = ({
  title,
  description,
  icon,
  children,
  moduleColor = 'default',
  span = 2,
  placeholderRowSpan = 12,
  isDragging = false,
  isPreviewSlot = false,
  fillHeight = false,
  showSpanControl = true,
  onColorChange,
  onRemove,
  onSpanChange,
  isAdmin = false,
}) => {
  const [isColorMenuOpen, setIsColorMenuOpen] = useState(false);
  const colorMenuRef = useRef<HTMLDivElement | null>(null);
  const activeTheme = useMemo(() => getDashboardCardTheme(moduleColor), [moduleColor]);

  useEffect(() => {
    if (!isColorMenuOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!colorMenuRef.current?.contains(target)) {
        setIsColorMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [isColorMenuOpen]);

  const currentSpanLabel = span === 1 ? '1칸' : span === 2 ? '2칸' : '4칸';
  const nextSpan = span === 1 ? 2 : span === 2 ? 4 : 1;
  const CurrentSpanIcon = span === 1 ? Square : span === 2 ? Columns2 : RectangleHorizontal;

  return (
    <div
      className={`dashboard-panel-surface ${activeTheme.surfaceClassName} overflow-hidden rounded-2xl transition-shadow duration-200 ${
        isDragging ? 'dashboard-drag-ring' : ''
      } ${
        isPreviewSlot ? 'dashboard-drop-placeholder' : ''
      } ${
        isPreviewSlot || fillHeight ? 'flex h-full flex-col' : ''
      }`}
    >
      <div
        className={`${activeTheme.dividerClassName} border-b p-3`}
        style={{ minHeight: 'var(--dashboard-card-header-min-height, 88px)' }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-start gap-3 pr-2">
            <div
              className={`dashboard-module-drag-handle ${activeTheme.handleClassName} flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
                isAdmin
                  ? 'cursor-grab active:cursor-grabbing'
                  : 'cursor-default opacity-70'
              }`}
              aria-label={isAdmin ? `Drag to reorder ${title}` : undefined}
              title={isAdmin ? 'Drag' : undefined}
            >
              <GrabHandleIcon className="h-4 w-4" />
            </div>
            <div className="dashboard-no-drag min-w-0 flex-1" data-no-drag="true">
              <div className="flex min-w-0 items-center gap-2">
                <span className={`${activeTheme.badgeClassName} dashboard-badge-label rounded-full px-2 py-0.5`}>
                  {icon}
                </span>
                <h3 className="dashboard-card-title truncate">{title}</h3>
              </div>
            </div>
          </div>
          <div
            className="dashboard-module-controls dashboard-no-drag flex shrink-0 items-center justify-end gap-2"
            data-no-drag="true"
          >
            {onColorChange && !isPreviewSlot && (
              <div
                ref={colorMenuRef}
                className="relative"
                data-no-drag="true"
              >
                <button
                  type="button"
                  onClick={() => {
                    if (!isAdmin) {
                      return;
                    }

                    setIsColorMenuOpen((current) => !current);
                  }}
                  disabled={!isAdmin}
                  data-no-drag="true"
                  className={`dashboard-button-neutral-muted flex h-9 w-9 items-center justify-center rounded-xl ${
                    !isAdmin ? 'cursor-not-allowed opacity-60' : ''
                  }`}
                  aria-label={`Change color for ${title}`}
                  title="Change color"
                >
                  <div className="relative flex items-center justify-center">
                    <Palette className="h-4 w-4" strokeWidth={1.5} />
                    <span className={`absolute -right-1 -bottom-1 h-2.5 w-2.5 rounded-full border border-white ${activeTheme.swatchClassName}`} />
                  </div>
                </button>
                {isColorMenuOpen && (
                  <div
                    className="dashboard-surface-raised absolute right-0 top-11 z-20 min-w-40 rounded-2xl border border-white/40 p-2 shadow-xl backdrop-blur-lg"
                    data-no-drag="true"
                  >
                    <div className="mb-2 px-2">
                      <p className="dashboard-section-label !text-[11px]">Color</p>
                    </div>
                    <div className="space-y-1">
                      {DASHBOARD_CARD_THEMES.map((theme) => (
                        <button
                          key={theme.id}
                          type="button"
                          onClick={() => {
                            onColorChange(theme.id);
                            setIsColorMenuOpen(false);
                          }}
                          data-no-drag="true"
                          className={`flex w-full items-center justify-between rounded-xl px-2 py-2 text-left transition hover:bg-white/60 ${
                            moduleColor === theme.id ? 'bg-white/55' : ''
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`h-3 w-3 rounded-full ${theme.swatchClassName}`} />
                            <span className="dashboard-body-text">{theme.label}</span>
                          </div>
                          {moduleColor === theme.id && <Check className="h-4 w-4 text-gray-600" strokeWidth={1.5} />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {onRemove && !isPreviewSlot && (
              <button
                type="button"
                onClick={onRemove}
                disabled={!isAdmin}
                data-no-drag="true"
                className={`dashboard-button-destructive flex h-9 w-9 items-center justify-center rounded-xl ${
                  !isAdmin ? 'cursor-not-allowed opacity-60' : ''
                }`}
                aria-label={`Remove ${title}`}
                title="Remove"
              >
                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
              </button>
            )}
            {showSpanControl && onSpanChange && !isPreviewSlot && (
              <button
                type="button"
                onClick={() => isAdmin && onSpanChange(nextSpan)}
                disabled={!isAdmin}
                data-no-drag="true"
                className={`dashboard-button-neutral-muted flex h-9 w-9 items-center justify-center rounded-xl ${
                  !isAdmin ? 'cursor-not-allowed opacity-60' : ''
                }`}
                aria-label={`${title} 너비 변경. 현재 ${currentSpanLabel}`}
                title={`너비 변경 · 현재 ${currentSpanLabel}`}
              >
                <CurrentSpanIcon className="h-4 w-4" strokeWidth={1.5} />
              </button>
            )}
          </div>
        </div>
        <p className="dashboard-no-drag dashboard-card-description mt-3" data-no-drag="true">{description}</p>
      </div>
      <div
        className={fillHeight ? 'flex min-h-0 flex-1 flex-col' : ''}
      >
        <div
          className={`${fillHeight ? 'flex min-h-0 flex-1 p-3 pt-3' : isPreviewSlot ? 'flex flex-1 p-3 pt-3' : 'p-3 pt-3'} ${
            isPreviewSlot ? '' : 'dashboard-no-drag'
          }`}
          data-no-drag={isPreviewSlot ? undefined : 'true'}
        >
          {isPreviewSlot ? (
            <div
              className="dashboard-drop-placeholder-body h-full w-full"
              style={{
                minHeight: `${Math.max(96, placeholderRowSpan * 12 + Math.max(placeholderRowSpan - 1, 0) * 12 - 112)}px`,
              }}
            />
          ) : fillHeight ? (
            <div className="min-h-0 flex-1 overflow-auto">
              {children}
            </div>
          ) : children}
        </div>
      </div>
    </div>
  );
};

export default ModuleCard;
